import React, { useState } from "react";
import axios from "axios";
import { Download, Truck, FileSpreadsheet, Upload } from "lucide-react";
import { useRef } from "react";

const FleetList = () => {
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const isExpired = (dateStr) => {
  if (!dateStr) return false;
  return new Date(dateStr) < new Date();
};

  const handleGetDetails = async () => {
    if (uploadedFile) {
      await handleBulkGetDetails();
      return;
    }
    if (!searchValue.trim()) {
      setError("Please enter a vehicle number");
      return;
    }
    setLoading(true);
    setError(null);
    setResult([]);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/fleet/details/${searchValue.trim().toUpperCase()}`
      );
      if (response.data.success) {
        setResult((prev) => [response.data.data, ...prev]);
        setSearchValue("");
      } else {
        setError(response.data.error || "Failed to fetch details");
      }
    } catch (err) {
      const rawError = err.response?.data?.error || err.message || "";
      if (rawError.includes("412") || rawError.includes("ULIP") || rawError.includes("Access denied") || rawError.includes("token")) {
        setError("Unable to fetch vehicle details. Please try again later.");
      } else {
        setError(rawError || "Something went wrong");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBulkGetDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append("excelFile", uploadedFile);
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/fleet/bulk`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (response.data.success) {
        setResult((prev) => [...response.data.data, ...prev]);
        setUploadedFile(null);
        fileInputRef.current.value = "";
      } else {
        setError(response.data.error || "Bulk upload failed");
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Bulk upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result.length) return;
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/fleet/export`,{ vehicleNumbers: result.map((r) => r.vehicleNo) },
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Fleet_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Download failed");
    }
  };

  const columns = [
    { label: "S No.",           key: "sno" },
    { label: "Owner Name",      key: "ownerName" },
    { label: "Vehicle No.",     key: "vehicleNo" },
    { label: "Chassis No.",     key: "chassisNo" },
    { label: "Reg. Date",       key: "regDate" },
    { label: "Insurance Start Date",   key: "insuranceStart" },
    { label: "Insurance End Date",       key: "insuranceEnd", checkExpiry: true },
    { label: "Insurance Company", key: "insuranceComp" },
    { label: "Permit",          key: "permit" },
    { label: "National Permit", key: "nationalPermit" },
    { label: "Fitness",         key: "fitness" },
    { label: "Tax",             key: "tax" },
    { label: "5 Year Permit",   key: "fiveYearPermit" },
    { label: "PUC",             key: "puc" },
    { label: "RTO Code",        key: "rtoCode" },
    { label: "GVW",             key: "gvw" }, 
  ];

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col items-center py-16 px-4">

      {/* Title */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center gap-2 mb-2">
          <Truck className="text-blue-600" size={28} />
          <h1 className="text-3xl font-bold text-slate-800">Fleet List</h1>
        </div>
      </div>

      {/* Search Card */}
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-md border border-slate-200 p-6 mb-6">
        
          <input
            type="text"
            placeholder="Enter vehicle number e.g. TN01AB1234"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && handleGetDetails()}
            disabled={!!uploadedFile}
            className="flex-1 border border-slate-200 mb-3 rounded-lg px-4 py-2.5 text-sm font-mono tracking-widest text-slate-800 placeholder:text-slate-300 placeholder:font-sans placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-blue-500 transition"
          />
          <div className="flex gap-3">
            <button
              onClick={() => fileInputRef.current.click()}
              disabled={loading}
              className="flex items-center gap-2 border border-slate-300 hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60 text-slate-600 hover:text-blue-600 text-sm font-semibold px-4 py-2.5 rounded-lg transition whitespace-nowrap"
            >
              <Upload size={14} />
              {uploadedFile ? `📄 ${uploadedFile.name.length > 15 ? uploadedFile.name.slice(0, 15) + "..." : uploadedFile.name}` : "Bulk Upload"}
            </button>

            <button
            onClick={handleGetDetails}
            disabled={loading || (!searchValue.trim() && !uploadedFile)}
            className=" bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-3 rounded-lg transition whitespace-nowrap"
          >
            {loading ? "Fetching..." : "GET DETAILS"}
          </button>
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) {
                setUploadedFile(file);
                setSearchValue("");
                setError(null);
              }
            }}
            accept=".xlsx,.xls,.csv"
            style={{ display: "none" }}
          />
          
        {error && (
            <div className="flex items-start gap-3 mt-4 px-4 py-3 bg-red-50 border border-red-100 rounded-xl">
              <div className="w-5 h-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-red-500 text-xs font-bold">!</span>
              </div>
              <div>
                <p className="text-sm font-semibold text-red-600">Unable to fetch vehicle details</p>
                <p className="text-xs text-red-400 mt-0.5">Please check the vehicle number and try again.</p>
              </div>
            </div>
        )}
      </div>

      {/* Empty State */}
      {!result.length && !loading && !error && (
        <div className="flex flex-col items-center text-slate-400 mt-16">
          <FileSpreadsheet size={44} className="mb-3 text-slate-300" />
          <p className="text-sm">No data yet. Search a vehicle number to get started.</p>
        </div>
      )}

      {/* Result Card + Table */}
      {result.length > 0 && (
        <div className="w-full max-w-5xl bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">

          {/* Card Header */}
          <div className="flex items-center justify-between px-6 py-4 bg-slate-50 border-b border-slate-200">
            <p className="font-semibold text-slate-800">Fleet List</p>

            <div className="flex items-center gap-3">
              <div>
                <p className="font-mono font-bold text-slate-800 tracking-wider">
                  {result.vehicleNo}
                </p>
                {result.ownerName && (
                  <p className="text-xs text-slate-400">{result.ownerName}</p>
                )}
              </div>
            </div>
            <button
              onClick={handleDownload}
              className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold px-4 py-2 rounded-lg transition"
            >
              <Download size={14} />
              Download Excel
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full m-10 border-collapse text-sm">
              <thead>
                <tr className="bg-blue-600 text-white">
                  {columns.map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-left font-semibold whitespace-nowrap"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.map((row, idx) => (
                <tr key={`${row.vehicleNo}-${idx}`} className="hover:bg-slate-50 transition">
                  {columns.map((col) => {
                    const expired = col.checkExpiry && isExpired(row[col.key]);
                    return (
                    <td
                      key={col.key}
                      className={`px-4 py-3 border-b border-slate-100 whitespace-nowrap text-xs font-medium ${
                              expired ? "text-red-600 bg-red-50" : "text-slate-700"
                            }`}
                    >
                      {row[col.key] || "—"}
                    </td>
                  );
                  })}
                </tr>
                ))}
              </tbody>
            </table>
          </div>

        </div>
      )}

    </div>
  );
};

export default FleetList;